/**
 * Server-side logo component for instant rendering.
 * Uses optimized WebP images with srcSet for retina displays.
 * This component is rendered server-side and appears in the initial HTML.
 * 
 * CRITICAL: This component must NEVER be client-side rendered.
 * It should only be used in server components.
 */

import Link from "next/link";
import { cn } from "@/lib/utils";

type LogoServerProps = {
  variant?: "default" | "compact";
  className?: string;
  href?: string;
};

/**
 * Server-side logo component that renders instantly with the page.
 * Uses optimized WebP images (64px/128px) for fast loading.
 */
export function LogoServer({ variant = "default", className, href = "/dashboard" }: LogoServerProps) {
  const content = (
    <div className={variant === "compact" ? "flex items-center justify-center" : cn("flex items-center", className)}>
      {variant === "default" ? (
        // Full logo with text (for headers)
        <div className={cn("relative", className)} style={{ height: '48px' }}>
          <img
            src="/logo-full-tight.webp"
            alt="GrowthSync"
            width={625}
            height={181}
            className="h-12 w-auto object-contain"
            loading="eager"
            decoding="async"
            style={{ display: 'block' }}
          />
        </div>
      ) : (
        // Icon only (for compact/icon-only contexts) - uses optimized WebP with srcSet
        <div 
          className={cn("relative shrink-0 flex items-center justify-center overflow-hidden bg-transparent", className || "h-9 w-9 md:h-11 md:w-11")}
        >
          <img
            src="/logo-icon-tight-256.webp"
            srcSet="/logo-icon-tight-128.webp 1x, /logo-icon-tight-256.webp 2x, /logo-icon-tight-384.webp 3x"
            alt="GrowthSync"
            width={96}
            height={96}
            className="h-full w-full object-contain object-center"
            loading="eager"
            decoding="async"
            style={{
              display: 'block',
              width: '100%',
              height: '100%'
            }}
          />
        </div>
      )}
    </div>
  );

  if (href) {
    return <Link href={href}>{content}</Link>;
  }

  return content;
}

